import React from 'react';
import { useStore } from 'core/react/useStore';
import { Toolbar, ToolbarItemPosition } from 'controls/react/layout/toolbar';
import { loadQualifierDetails } from 'areas/services/designer/qualifierWizard/wizardSave';
import { WidgetInfo } from 'controls/react/widgetInfoContainer';
import { translations as sharedLocalization } from 'controls/designer/features/widgets/translations';
import { ChartSettings, ChartSettingsFlags } from 'areas/service-boards/chartSettings/chartSettings';
import { DownloadOutlined } from '@ant-design/icons';
import { ColumnDefinition, exportToCSV } from 'tools/exportHelpers/csvExporter';
import {
	legacyPeriods,
	TimePeriod,
	TimePeriodAppearance,
	TimePeriodSelector,
} from 'controls/react/form/timePeriodSelector';
import { observer } from 'mobx-react';
import { makeAutoObservable, observable } from 'mobx';
import { MobxManager } from 'framework/mobx-integration';
import { TimePeriodType } from 'controls/react/form/timePeriodType';
import { AntPopover } from 'controls/react/ant/antPopover';
import { translator } from 'core/localization';

export const i18n = translator(
	{
		'Y-axis': {
			no: 'Y-akse',
		},
		'Dynamic': {
			no: 'Dynamikk',
		},
		'Zero': {
			no: 'Null',
		},
		'Export data to CSV': { no: 'Eksporter data til CSV' },
		'Chart settings': { no: 'Grafikinnstillinger' },
	},
	sharedLocalization,
);

export enum WidgetToolboxItem {
	Info = 'info',
	Settings = 'settings',
	TimeSelector = 'timeselector',
	CsvExport = 'csvexport',
}

export type Dict = { [id: string]: string | number | boolean | Dict | DOMRect | Dict[] };

export type WidgetType = {
	preProcessData?: (data: Dict) => Dict;
	instanceConfiguration?: Dict;
	type?: string;
	dataItem?: Dict;
	qualifier?: Dict;
	qualifiers?: Dict[];
	hoverInfoQualifier?: Dict;
	defaultQualifierUnitType?: string;
	getMetricInfoUrl?: (data: Dict) => string;
	onNewTimeSelectorChanged?: (period: TimePeriod) => void;
	onSettingsApply?: (value: ChartSettings) => void;
	chartData?: Dict[];
} & ChartSettingsFlags;

export type WidgetToolboxProps = {
	controls: WidgetToolboxItem[];
	widget: WidgetType;
	widgetId: string;
	onCsvExport?: () => void;
	options?: {
		timeSelectorAppearance: TimePeriodAppearance;
	};
};

export class WidgetToolboxStore {
	elements: { content: React.ReactNode; key: string }[];
	widgetId: string;
	displayInfo: boolean;
	displaySettings: ChartSettings;
	widgetInfoProps: Dict;
	widget: WidgetType;
	controls: WidgetToolboxItem[];
	timePeriod: TimePeriod;
	onCsvExport?: () => void;
	options?: {
		timeSelectorAppearance: TimePeriodAppearance;
	};

	mobx = new MobxManager();

	constructor(props: WidgetToolboxProps) {
		Object.assign(this, props);
		// elements holds React nodes; deep-observing them breaks React 19's internal owner-stack tracking
		// (Illegal invocation on _debugTask.run when MobX proxies the element objects).
		makeAutoObservable(this, { elements: observable.ref });
		this.mobx.reaction(
			() => this.widget.instanceConfiguration?.period,
			() => {
				this.timePeriod = (
					this.widget.instanceConfiguration?.period
						? {
								period: this.widget.instanceConfiguration.period,
								startDate: this.widget.instanceConfiguration.startDate,
								endDate: this.widget.instanceConfiguration.endDate,
								intervalType: this.widget.instanceConfiguration.intervalType,
								intervalValue: this.widget.instanceConfiguration.intervalValue,
								lastNValue: this.widget.instanceConfiguration.lastNValue,
							}
						: {
								period: TimePeriodType.Last24Hours,
							}
				) as TimePeriod;
			},
			{ fireImmediately: true },
		);
	}

	init() {
		this.elements = this.controls.map((x) => ({ content: this.getContent(x), key: x }));
	}

	getContent(key: WidgetToolboxItem) {
		switch (key) {
			case WidgetToolboxItem.TimeSelector:
				return <WidgetTimeSelector store={this} position={ToolbarItemPosition.AT_THE_END} />;
			case WidgetToolboxItem.Info:
				return <InfoIcon store={this} position={ToolbarItemPosition.AT_THE_END} />;
			case WidgetToolboxItem.Settings:
				return <SettingsIcon store={this} position={ToolbarItemPosition.AT_THE_END} />;
			case WidgetToolboxItem.CsvExport:
				return <ExportCsv store={this} position={ToolbarItemPosition.AT_THE_END} />;
			default:
				return null;
		}
	}

	destroy() {
		this.mobx.destroy();
	}

	onInfoSignHover = async () => {
		if (this.displayInfo) {
			this.displayInfo = false;
			return;
		}

		let config: Dict;
		let data: Dict, infoObj, type;

		if (['service-gauge', 'metrics', 'gauge', 'service-single-graph'].indexOf(this.widget.type) !== -1) {
			config = this.widget.instanceConfiguration;
			infoObj = {
				accountId: config.accountId,
				serviceId: config.serviceId,
				serviceElementId: config.serviceElementId,
				serviceQualifierId: config.serviceQualifierId,
			};
		} else {
			const dataItem: Dict = this.widget.dataItem;
			if (dataItem && dataItem.type === 'COLLECTOR') {
				//asset details collector
				config = this.widget.dataItem;
				infoObj = {
					accountId: config.accountId,
					serviceId: config.parentId,
					serviceElementId: config.elementId,
					serviceQualifierId: config.qualifierId,
				};
			} else if (this.widget.type === 'graph_gauge' || this.widget.type === 'metric-gauge') {
				//metric gauge
				data = (this.widget.qualifier || this.widget.instanceConfiguration.metricsItem) as Dict;
			} else if (['asset_gauge', 'kpi_gauge', 'health-gauge', 'kpi-gauge'].indexOf(this.widget.type) !== -1) {
				//health/kpi gauge
				data = this.widget.instanceConfiguration;
			} else if (this.widget.type === 'metric-single-graph') {
				data = this.widget.qualifiers?.length ? this.widget.qualifiers[0] : this.widget.hoverInfoQualifier;
			} else {
				data = this.widget.hoverInfoQualifier;
			}
		}

		if (data) {
			delete data.isDashboard;
		}

		if (infoObj) {
			type = 'request';
			data = await loadQualifierDetails(this.widget.getMetricInfoUrl(infoObj));
		} else {
			data = this.widget.preProcessData?.(data);
			type = 'preloaded';
		}
		const flags: Dict = {
			hasAggregation: this.widget.hasAggregateSelector,
			hasChartType: this.widget.hasChartTypeSelector,
			hasMissingData: this.widget.hasMissingData,
			hasRegression: this.widget.hasRegression,
			hasThreshold: this.widget.hasThreshold,
		};
		let isAnyFlag = false;
		for (const key in flags) {
			if (flags[key]) {
				isAnyFlag = true;
			}
		}
		if (this.widget.instanceConfiguration) {
			this.widget.instanceConfiguration.aggregationType ??= this.widget.qualifiers?.every(
				(x: Dict) => x.metricType == 'Boolean',
			)
				? 'low'
				: 'high';
		}

		this.widgetInfoProps = {
			data,
			type,
			chartSettings: this.widget.instanceConfiguration,
			flags,
			isAnyFlag,
		};
		this.displayInfo = true;
	};

	onSettingsOpenChanged = () => {
		if (this.displaySettings) {
			this.displaySettings = null;
			return;
		}
		this.displaySettings = Object.assign({}, this.widget.instanceConfiguration, {
			unitType:
				this.widget.instanceConfiguration.tempUnitType || this.widget.instanceConfiguration.unitType || 'AUTOSCALING',
			defaultQualifierUnitType: this.widget.defaultQualifierUnitType,
		}) as unknown as ChartSettings;
	};

	onSettingsApply = (value: ChartSettings) => {
		this.widget.onSettingsApply(value);
		this.onSettingsOpenChanged();
	};

	exportCSV = () => {
		if (this.onCsvExport) {
			this.onCsvExport();
			return;
		}

		const data: { [id: string]: Dict[] }[] = this.widget.chartData.filter((x) => x.qualifierId) as {
			[id: string]: Dict[];
		}[];

		let index = 0;
		const resultData: { timestamp: number; [qualifierId: string]: number | null }[] = [];
		while (data.some((x) => x.data.length >= index + 1)) {
			data.forEach((x) => {
				if (!x.data[index]) {
					return;
				}
				let current: { timestamp: number; [qualifierId: string]: number | null } = resultData.find(
					(y) => y.timestamp === x.data[index][0],
				);
				if (!current) {
					current = { timestamp: x.data[index][0] as number };
					data.forEach((x) => (current[x.qualifierId as unknown as string] = null));
					resultData.push(current);
				}
				current[x.qualifierId as unknown as string] = x.data[index][1] as unknown as number;
			});
			index++;
		}
		resultData.sort((a, b) => a.timestamp - b.timestamp);
		const columns: ColumnDefinition[] = [
			{ field: 'timestamp', title: 'timestamp' },
			...data.map(
				(x) =>
					({
						field: x.qualifierId,
						title: x.name || `${x.toolTipAssetName}/${x.toolTipMetricName}`,
					}) as unknown as ColumnDefinition,
			),
		];
		exportToCSV(resultData, columns, {});
	};

	onNewTimeSelectorChanged = (period: TimePeriod) => {
		this.timePeriod = period;
		this.widget.onNewTimeSelectorChanged(period);
	};
}

export const WidgetToolbox = observer((props: WidgetToolboxProps) => {
	const store = useStore(() => new WidgetToolboxStore(props), { deps: [props.options] });
	return (
		<Toolbar appearance={'transparent'}>
			{store.elements?.map((x) =>
				x.content ? (
					<div key={x.key} position={ToolbarItemPosition.AT_THE_END}>
						{x.content}
					</div>
				) : null,
			)}
		</Toolbar>
	);
});

const InfoIcon = observer(({ store }: { store: WidgetToolboxStore }) => {
	return (
		<AntPopover
			content={<WidgetInfo {...store.widgetInfoProps} />}
			open={store.displayInfo}
			trigger={'hover'}
			onOpenChange={store.onInfoSignHover}
		>
			<span className="glyphicons info-sign"></span>
		</AntPopover>
	);
});

const SettingsIcon = observer(({ store }: { store: WidgetToolboxStore }) => {
	return (
		<AntPopover
			content={<ChartSettings flags={store.widget} value={store.displaySettings} onChange={store.onSettingsApply} />}
			open={!!store.displaySettings}
			styles={{ root: { width: '516px' } }}
			trigger={'click'}
			destroyOnHidden={true}
			onOpenChange={store.onSettingsOpenChanged}
		>
			<span className={'k-icon k-i-toggle'} title={i18n('Chart settings')} />
		</AntPopover>
	);
});

const ExportCsv = observer(({ store }: { store: WidgetToolboxStore }) => {
	return <DownloadOutlined title={i18n('Export data to CSV')} onClick={() => store.exportCSV()} />;
});

const WidgetTimeSelector = observer(({ store }: { store: WidgetToolboxStore }) => {
	return (
		<TimePeriodSelector
			periods={legacyPeriods}
			size={'small'}
			appearance={store.options?.timeSelectorAppearance ?? TimePeriodAppearance.Buttons}
			value={store.timePeriod}
			noWrap={true}
			onChange={store.onNewTimeSelectorChanged}
		/>
	);
});
